import { API } from "@swivl-mono/api-helper";
import { DateHelper } from "@swivl-mono/helpers";
import { CallRecordingResponse, UserInterface } from "@swivl-mono/types";
import { useEffect, useState } from "react";
import { TbExternalLink } from "react-icons/tb";
import { Link } from "react-router-dom";

import { CallDirectionIcon } from "../../../../../Components/Icons/CallDirectionIcon";
import { formatDuration } from "../../../../../Helpers/Time/formatDuration";
import { Member } from "../../../../../Models/Member/Member.Current";

interface UserDetailsRecordingsViewProps {
  user: UserInterface;
}

export function UserDetailsRecordingsView({ user }: UserDetailsRecordingsViewProps): React.ReactElement {
  const [recordings, setRecordings] = useState<CallRecordingResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    const orgId = Member.orgId;
    if (!orgId) return;
    void (async () => {
      setIsLoading(true);
      setError(undefined);
      try {
        const res = await API.Calls.getCallRecordingsListByOrg(orgId, { userId: user.id, limit: 50 });
        setRecordings(res.data);
      } catch {
        setError("Failed to load recordings.");
      } finally {
        setIsLoading(false);
      }
    })();
  }, [user.id, Member.orgId]);

  if (isLoading) return <p className="text-sm text-grey-600 italic">Loading recordings...</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!recordings.length) return <p className="text-sm text-grey-600 italic">No recordings for this user.</p>;

  return (
    <div className="flex w-full flex-col gap-3 pb-4">
      {recordings.map((recording) => (
        <RecordingRow key={recording.callSid} recording={recording} />
      ))}
    </div>
  );
}

function buildRecordingDeepLinkParams(recording: CallRecordingResponse): URLSearchParams {
  const params = new URLSearchParams({ recordingId: recording.callSid, search: recording.userName });
  if (recording.userId !== undefined) {
    params.set("userId", String(recording.userId));
  }
  return params;
}

function RecordingRow({ recording }: { recording: CallRecordingResponse }): React.ReactElement {
  return (
    <div className="flex items-center justify-between rounded-sm border border-grey-300 bg-white p-3">
      <div className="flex flex-col gap-1.5 font-mono text-xs">
        <div className="flex items-center gap-2 font-bold text-grey-800">
          <CallDirectionIcon direction={recording.callDirection} className="h-3.5 w-3.5" />
          <span>{formatDuration(recording.callDuration || 0)}</span>
          <span className="font-normal">{recording.callOutcome}</span>
        </div>
        <div className="text-grey-600">
          {DateHelper.formatDateTime(recording.createdAt)}
          {recording.locationName ? ` · ${recording.locationName}` : ""}
        </div>
      </div>
      <Link
        to={`/contact-center/recordings?${buildRecordingDeepLinkParams(recording).toString()}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open in Recordings"
      >
        <TbExternalLink className="h-5 w-5 text-grey-600" />
      </Link>
    </div>
  );
}
